<script setup lang="ts">
import SalaryValueDisplay from './SalaryValueDisplay.vue'
import { formatSalary } from '~/utils/formatters'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import BellCurveCanvas from '~/components/charts/BellCurveCanvas.vue'

interface Props {
  median: number
  min?: number
  max?: number
  height?: number
  width?: number
  formatValue?: (value: number) => string
}
const {
  median,
  min = undefined,
  max = undefined,
  height = 150,
  width = 300,
  formatValue = undefined
} = defineProps<Props>()
const { activePeriod } = useGlobalSalaryPeriod()
const salaryValues = computed(() => ({
  min: min || median * 0.7,
  max: max || median * 1.3,
  median: median
}))
const formatValueFn = (value: number): string => {
  if (formatValue) {
    return formatValue(value)
  }
  return formatSalary(value, activePeriod.value)
}
</script>

<template>
  <div class="salary-distribution-chart relative w-full">
    <SalaryValueDisplay
      label="Median"
      :value="formatValueFn(salaryValues.median)"
      :is-highlighted="true"
      class="mb-1"
    />
    <BellCurveCanvas
      :min="salaryValues.min"
      :median="salaryValues.median"
      :max="salaryValues.max"
      :height="height"
      :width="width"
    />
    <div class="flex justify-between text-xs text-gray-500 mt-2">
      <SalaryValueDisplay
        label="Low"
        :value="formatValueFn(salaryValues.min)"
      />
      <SalaryValueDisplay
        label="High"
        :value="formatValueFn(salaryValues.max)"
      />
    </div>
  </div>
</template>

<style scoped>
.salary-distribution-chart {
  width: 100%;
  min-height: 200px; /* Prevent CLS during chart load */
  margin-bottom: 0.5rem;
}
</style>
